import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/lib/query-keys'

export interface MachineLocation {
  id: number
  name: string
}

export interface MachineOrganisation {
  id: number
  name: string
}

export interface Machine {
  id: number
  code: string
  name: string
  type: string
  type_name: string
  status: string
  status_category: 'operational' | 'needs-attention' | 'offline'
  stock: number
  capacity: number
  empty_slots: number
  location: MachineLocation | null
  organisation: MachineOrganisation | null
}

export interface SlotPricing {
  price_including_vat: number
}

export interface SlotProduct {
  id: number
  name: string
  packaging_name: string | null
  image_url: string | null
  pricing: SlotPricing | null
  sales_count_30d: number
}

export interface Slot {
  id: number
  position: number
  capacity: number
  stock: number
  stock_percentage: number
  user_editable: boolean
  product: SlotProduct | null
  next_product: SlotProduct | null
}

export interface Shelve {
  position: number
  code: string
  slots: Slot[]
}

export interface SalesMonth {
  month: string
  transactions: number
  revenue?: number
}

export interface SalesOverviewRevenue {
  type: 'revenue'
  total: number
  months: SalesMonth[]
}

export interface SalesOverviewTransactions {
  type: 'transactions'
  total: number
  months: SalesMonth[]
}

export type SalesOverview = SalesOverviewRevenue | SalesOverviewTransactions

export interface MachineDetail extends Machine {
  location_id: number | null
  stock_percentage: number | null
  temperature: number | null
  filling_level: number | null
  auto_refill: boolean
  last_order: string | null
  installation_date: string | null
  subscription_name: string | null
  subscription_type: 'none' | 'essential' | 'happiness' | 'revenue' | null
  shelves: Shelve[]
  sales_overview: SalesOverview | null
}

export async function fetchMachines(params?: {
  search?: string
  status?: string
  organisation_id?: number[]
}): Promise<Machine[]> {
  const response = await api.get<{ data: Machine[] }>('/machines', { params })
  return response.data
}

export async function fetchMachine(machineCode: string): Promise<MachineDetail> {
  return api.get<MachineDetail>(`/machines/${machineCode}`)
}

export function machinesQueryOptions(params?: {
  search?: string
  status?: string
  organisation_id?: number[]
}) {
  return queryOptions({
    queryKey: queryKeys.machines.list(params),
    queryFn: async () => fetchMachines(params),
  })
}

export async function updateMachine(
  machineCode: string,
  data: { name: string; location_id: number | null },
): Promise<MachineDetail> {
  return api.patch<MachineDetail>(`/machines/${machineCode}`, { body: data })
}

export function machineQueryOptions(machineCode: string) {
  return queryOptions({
    queryKey: queryKeys.machines.detail(machineCode),
    queryFn: async () => fetchMachine(machineCode),
  })
}
